import { DOCUMENT, ErrorHandler, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { VersionDesplegadaService } from './version-desplegada.service';

/**
 * Cómo se presenta el fallo según el navegador y la versión de Angular.
 *
 * No hay un tipo de error que reconocer: llega un `TypeError` con el mensaje del `import()` que no pudo
 * resolverse, y cada motor lo redacta a su manera. Se buscan los tres textos que cubren Chromium, Firefox
 * y el `ChunkLoadError` histórico. Reconocer de más aquí no es grave —la recuperación comprueba después
 * si de verdad se ha desplegado algo, y si no, no hace nada—, pero reconocer de menos deja la pantalla
 * muerta, que es el fallo que se venía a evitar.
 */
const SENALES = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'chunkloaderror',
  'importing a module script failed',
];

/** True si el error es de los que deja una pantalla sin pintar tras un despliegue encima. */
export function esFalloDeCargaPerezosa(error: unknown): boolean {
  const mensaje =
    error instanceof Error
      ? `${error.name} ${error.message}`
      : typeof error === 'string'
        ? error
        : '';

  const normalizado = mensaje.toLowerCase();
  return SENALES.some((senal) => normalizado.includes(senal));
}

/**
 * Manejador global que rescata a quien se queda sin pantalla por un despliegue encima.
 *
 * <h3>Qué problema resuelve</h3>
 * El despliegue del front hace `robocopy /MIR`: borra los ficheros de la versión anterior. Una sesión
 * abierta que navegue a una pantalla que aún no había visitado pide un chunk que ya no existe, y se queda
 * donde estaba, sin pantalla y sin explicación.
 *
 * <h3>Por qué no se traga el error</h3>
 * Siempre delega en el manejador de siempre, haya recarga o no. Si el fallo no era por un despliegue
 * —porque la versión publicada es la misma—, la única señal de que algo va mal es ese error: taparlo
 * dejaría un problema real sin rastro.
 */
@Injectable()
export class ChunkPerdidoErrorHandler implements ErrorHandler {
  private readonly version = inject(VersionDesplegadaService);
  private readonly router = inject(Router);
  private readonly documento = inject(DOCUMENT);

  /** El de Angular, que es el que escribe en consola. */
  private readonly original = new ErrorHandler();

  handleError(error: unknown): void {
    if (esFalloDeCargaPerezosa(error)) {
      void this.version.recuperarDeCargaPerezosa(this.rutaPedida());
    }

    this.original.handleError(error);
  }

  /**
   * Adónde se iba.
   *
   * Se recarga ahí y no en la pantalla actual: al fallar la navegación uno se queda en la anterior, y
   * recargar esa le dejaría lejos de donde iba, teniendo que volver a buscar el camino.
   */
  private rutaPedida(): string {
    const navegacion = this.router.getCurrentNavigation();
    const destino = navegacion?.finalUrl ?? navegacion?.extractedUrl;

    if (destino) return this.router.serializeUrl(destino);

    return this.documento.defaultView?.location.href ?? '/';
  }
}
